import React from 'react';
import './MartLogo.css';

interface MartLogoProps {
    /** Wordmark scale. `lg` is for brand plates, not navigation. */
    size?: 'md' | 'lg';
    /** Descriptor under the wordmark; pass null to drop it. */
    sub?: string | null;
}

/**
 * Kifayti SuperMart lockup — the retail division's mark.
 *
 * A shopping bag holds the first two letters of the name and the rest of the
 * word runs out to its right. The bag is drawn as vector geometry on a fixed
 * 100 x 140 grid (a near-square body under a 40-unit arch), so its proportions
 * are designed rather than dictated by whatever width "Ki" happens to measure.
 * The letterforms stay live text in the site's own typeface, which keeps "Ki"
 * and "fayti" identical in weight and shape.
 *
 * This is deliberately NOT the company mark: the bag speaks for the stores,
 * while the parent business is mostly software. See Logo.tsx for that.
 */
const MartLogo: React.FC<MartLogoProps> = ({ size = 'md', sub = 'SuperMart' }) => (
    <span className={`mart-logo mart-logo--${size}`}>
        <span className="mart-logo__word">
            <span className="mart-logo__bag">
                <svg
                    className="mart-logo__bag-art"
                    viewBox="0 0 100 140"
                    preserveAspectRatio="none"
                    aria-hidden="true"
                    focusable="false"
                >
                    {/* Handle first: the body paints over its ends, so the arch
                        reads as passing behind the bag's top edge. Measured off
                        the artwork — a wide, shallow arc (rx 42.5 / ry 23.5) on
                        a heavy 15-unit stroke. Any rounder and it turns into a
                        padlock shackle. */}
                    <path
                        className="mart-logo__bag-handle"
                        d="M7.5 40a42.5 23.5 0 0 1 85 0"
                        fill="none"
                        strokeWidth="15"
                    />
                    {/* Body: near-square top corners, softer bottom ones. */}
                    <path
                        className="mart-logo__bag-body"
                        d="M5 40h90a5 5 0 0 1 5 5v82a13 13 0 0 1-13 13H13a13 13 0 0 1-13-13V45a5 5 0 0 1 5-5z"
                    />
                </svg>
                <span className="mart-logo__bag-text">Ki</span>
            </span>
            <span className="mart-logo__tail">fayti</span>
        </span>

        {sub && <span className="mart-logo__sub">{sub}</span>}
    </span>
);

export default MartLogo;
